import { useIntl } from 'umi';
import { useState, useEffect } from 'react';

const useGCPI18n = () => {
  const { formatMessage } = useIntl();

  // 1. 使用 useState 初始化，而不是 useMemo, 无法直接监听localStorage变化
  const [GCPI18nText, setGCPI18nText] = useState(() => {
    try {
      return JSON.parse(window.localStorage.getItem('GCP_i18n') || '{}');
    } catch (e) {
      return {};
    }
  });
  // 定义处理函数
  const handleStorageChange = () => {
    try {
      const newValue = JSON.parse(window.localStorage.getItem('GCP_i18n') || '{}');
      setGCPI18nText(newValue);
    } catch (e) {
      setGCPI18nText({});
    }
  };
  useEffect(() => {
    // 2. 监听 storage 事件 (注意：这通常只在其他标签页修改 storage 时触发)
    window.addEventListener('storage', handleStorageChange);
    // 3. 如果当前标签页修改了 localStorage，需要手动触发自定义事件
    window.addEventListener('i18n-storage-change', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('i18n-storage-change', handleStorageChange);
    };
  }, []);
  const GCPI18n = (text, params) => {
    let label = '';

    // 处理不同格式的输入
    if (typeof text === 'string') {
      label = text;
    } else if (text?.id) {
      label = text.id;
    } else {
      return '';
    }

    // 处理参数，保留React元素的样式，仅对模板字符串替换时转换为文本
    const processedParams = params ? { ...params } : {};

    // 获取模板文本
    const template = GCPI18nText[label];
    // 使用本地存储的模板（如果存在）
    if (template) {
      // 检查参数中是否包含React元素
      const hasReactElements =
        processedParams &&
        Object.values(processedParams).some(
          value => value && typeof value === 'object' && value.$$typeof,
        );

      if (hasReactElements) {
        // 如果包含React元素，需要返回React元素数组而不是字符串
        // 手动解析模板中的 {key} 占位符，替代正则 split
        const parts = [];
        let current = '';
        let idx = 0;
        while (idx < template.length) {
          if (template[idx] === '{') {
            let j = idx + 1;
            let found = false;
            while (j < template.length) {
              if (template[j] === '}') {
                parts.push(current);
                parts.push(template.substring(idx + 1, j));
                current = '';
                idx = j + 1;
                found = true;
                break;
              }
              j += 1;
            }
            if (!found) {
              current += template[idx];
              idx += 1;
            }
          } else {
            current += template[idx];
            idx += 1;
          }
        }
        if (current) {
          parts.push(current);
        }
        const result = [];

        for (let i = 0; i < parts.length; i += 1) {
          if (i % 2 === 0) {
            // 普通文本部分
            if (parts[i]) {
              result.push(parts[i]);
            }
          } else {
            // 参数替换部分
            const key = parts[i].trim();
            if (processedParams && Object.prototype.hasOwnProperty.call(processedParams, key)) {
              result.push(processedParams[key]);
            } else {
              result.push(`{${parts[i]}}`);
            }
          }
        }

        // 为数组添加自定义的 toString 方法，处理模板字符串调用
        // eslint-disable-next-line func-names
        result.toString = function () {
          return this.map(item => {
            if (item && typeof item === 'object' && item.$$typeof) {
              // React元素转换为文本内容
              if (item.props && item.props.children) {
                return typeof item.props.children === 'string' ? item.props.children : '';
              }
              return '';
            }
            return item;
          }).join('');
        };

        return result;
      }
      // 手动替换模板中的 {key} 占位符，替代正则 replace
      let result = '';
      let pos = 0;
      while (pos < template.length) {
        if (template[pos] === '{') {
          const end = template.indexOf('}', pos + 1);
          if (end !== -1) {
            const key = template.substring(pos + 1, end).trim();
            if (processedParams && Object.prototype.hasOwnProperty.call(processedParams, key)) {
              result += processedParams[key];
            } else {
              result += template.substring(pos, end + 1);
            }
            pos = end + 1;
          } else {
            result += template[pos];
            pos += 1;
          }
        } else {
          result += template[pos];
          pos += 1;
        }
      }
      return result;
    }
    // 回退到 react-intl
    return formatMessage({ id: label }, processedParams);
  };

  return GCPI18n;
};

export default useGCPI18n;
